/**
 * One install record per signed-in account. Firestore create is owner-only;
 * a second create is treated as already counted.
 */

export const APP_INSTALLS_COLLECTION = 'appInstalls';
export const APP_INSTALL_RECORDED_KEY = 'm2b_app_install_recorded';
export const APP_INSTALL_PENDING_KEY = 'm2b_app_install_pending';

const SOURCES = new Set(['appinstalled', 'standalone']);
const PLATFORMS = new Set(['ios', 'android', 'desktop']);

const flights = new Map();

function storageGet(storage, key) {
  try {
    return storage?.getItem(key) || '';
  } catch {
    return '';
  }
}

function storageSet(storage, key, value) {
  try {
    storage?.setItem(key, value);
  } catch {
    /* private mode */
  }
}

function storageRemove(storage, key) {
  try {
    storage?.removeItem(key);
  } catch {
    /* private mode */
  }
}

/** Same signals as `isIosDevice` in webPush.js. */
function isIosNav(nav) {
  if (!nav) return false;
  const ua = nav.userAgent || '';
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  return nav.platform === 'MacIntel' && Number(nav.maxTouchPoints) > 1;
}

/**
 * @param {Navigator|undefined} nav
 * @returns {'ios'|'android'|'desktop'}
 */
export function installPlatform(nav) {
  if (isIosNav(nav)) return 'ios';
  if (/Android/i.test(nav?.userAgent || '')) return 'android';
  return 'desktop';
}

/**
 * @param {string|null|undefined} uid
 * @param {Storage|undefined} storage
 */
export function appInstallAlreadyRecorded(uid, storage) {
  return Boolean(uid) && storageGet(storage, APP_INSTALL_RECORDED_KEY) === uid;
}

/**
 * @param {string} uid
 * @param {Storage|undefined} storage
 */
export function markAppInstallRecorded(uid, storage) {
  storageSet(storage, APP_INSTALL_RECORDED_KEY, uid);
  storageRemove(storage, APP_INSTALL_PENDING_KEY);
}

/**
 * @param {Storage|undefined} storage
 * @returns {'appinstalled'|'standalone'|null}
 */
export function readPendingInstallSource(storage) {
  const value = storageGet(storage, APP_INSTALL_PENDING_KEY);
  return SOURCES.has(value) ? value : null;
}

/**
 * @param {'appinstalled'|'standalone'} source
 * @param {Storage|undefined} storage
 */
export function markPendingInstallSource(source, storage) {
  if (!SOURCES.has(source)) return;
  storageSet(storage, APP_INSTALL_PENDING_KEY, source);
}

/**
 * Source to persist, or null when this visit should not write.
 * @param {{ uid?: string|null, alreadyRecorded?: boolean, standalone?: boolean, appInstalled?: boolean, pendingSource?: string|null }} input
 * @returns {'appinstalled'|'standalone'|null}
 */
export function resolveAppInstallSource({
  uid,
  alreadyRecorded = false,
  standalone = false,
  appInstalled = false,
  pendingSource = null,
} = {}) {
  if (!uid || alreadyRecorded) return null;
  if (appInstalled) return 'appinstalled';
  if (standalone) return 'standalone';
  if (SOURCES.has(pendingSource)) return pendingSource;
  return null;
}

/**
 * Flag to keep when the install happens before sign-in.
 * @param {{ standalone?: boolean, appInstalled?: boolean }} input
 * @returns {'appinstalled'|'standalone'|null}
 */
export function pendingSourceWhileSignedOut({ standalone = false, appInstalled = false } = {}) {
  if (appInstalled) return 'appinstalled';
  if (standalone) return 'standalone';
  return null;
}

/**
 * @param {{ uid?: string, source?: string, platform?: string, installedAt?: unknown }} fields
 * @returns {object|null}
 */
export function buildAppInstallDoc({ uid, source, platform, installedAt } = {}) {
  if (!uid || !SOURCES.has(source) || !PLATFORMS.has(platform)) return null;
  return { uid, source, platform, installedAt };
}

async function writeAppInstallOnce({
  uid,
  source,
  platform,
  storage,
  installedAt,
  readDoc,
  createDoc,
}) {
  if (appInstallAlreadyRecorded(uid, storage)) return { status: 'stored' };
  const payload = buildAppInstallDoc({ uid, source, platform, installedAt });
  if (!payload || typeof readDoc !== 'function' || typeof createDoc !== 'function') {
    return { status: 'skip' };
  }
  try {
    const exists = await readDoc(uid);
    if (exists) {
      markAppInstallRecorded(uid, storage);
      return { status: 'exists' };
    }
  } catch (err) {
    console.warn('recordAppInstall failed:', err?.message || err);
    return { status: 'error' };
  }
  try {
    await createDoc(uid, payload);
    markAppInstallRecorded(uid, storage);
    return { status: 'wrote' };
  } catch (err) {
    const code = String(err?.code || '');
    if (code === 'permission-denied' || code === 'already-exists') {
      markAppInstallRecorded(uid, storage);
      return { status: 'exists' };
    }
    console.warn('recordAppInstall failed:', err?.message || err);
    return { status: 'error' };
  }
}

/**
 * Create `appInstalls/{uid}` once. Later calls for the same uid join the
 * in-flight write or no-op after localStorage is set.
 * @param {object} args
 * @returns {Promise<{ status: string }>}
 */
export function recordAppInstallOnce(args) {
  const uid = args?.uid;
  if (!uid) return Promise.resolve({ status: 'skip' });
  const existing = flights.get(uid);
  if (existing) return existing;
  const flight = writeAppInstallOnce(args).finally(() => {
    flights.delete(uid);
  });
  flights.set(uid, flight);
  return flight;
}
